// src/products/dataroom/hooks/useDataroomApp.js
import { getProjectSlug } from '../utils/slugUtils';
// Custom hook for managing DataroomApp state and operations

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectManagement } from './useProjectManagement';
import { usePermissionManagement } from './usePermissionManagement';
import { useActivityFeed } from './useActivityFeed';
import { useGlobalStore } from '../../../state/globalStore';
import { supabase } from '../../../config';
import permissionService from '../services/permissionService';

export const useDataroomApp = () => {
  const navigate = useNavigate();
  
  // Use global store for user management
  const { user, userProfile, loading: userLoading, initUser } = useGlobalStore();
  
  const [userRole, setUserRole] = useState('creator'); // Default to creator for testing
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modals, setModals] = useState({});

  // Use other hooks
  const projectManagement = useProjectManagement(user?.id);
  const permissionManagement = usePermissionManagement(selectedProjectId);
  const activityFeed = useActivityFeed(selectedProjectId);

  // Add message with proper cleanup
  const addMessage = useCallback((message, type = 'info') => {
    const id = Date.now();
    setMessages(prev => [...prev, { id, message, type, timestamp: new Date() }]);
    
    // Auto-remove after 5 seconds with cleanup tracking
    const timeoutId = setTimeout(() => {
      setMessages(prev => prev.filter(m => m.id !== id));
    }, 5000);
    
    // Return cleanup function (though not used in this context, it's good practice)
    return () => clearTimeout(timeoutId);
  }, []);


  // Load user data
  const loadUserData = useCallback(async () => {
    try {
      // Initialize user from global store
      await initUser();
    } catch (error) {
      console.error('Error loading user data:', error);
      addMessage('Failed to load user data', 'error');
    }
  }, [initUser, addMessage]);


  // Open modal
  const openModal = useCallback((modalName, data = null) => {
    setModals(prev => ({
      ...prev,
      [modalName]: data !== null ? data : true
    }));
  }, []);

  // Close modal
  const closeModal = useCallback((modalName) => {
    setModals(prev => ({
      ...prev,
      [modalName]: false
    }));
  }, []);

  // Handle project selection
  const handleProjectSelect = useCallback(async (projectId) => {
    try {
      setSelectedProjectId(projectId);
      
      // Find project to get slug
      const project = projectManagement.projects.find(p => p.id === projectId);
      const slug = project ? getProjectSlug(project) : projectId.toString();
      
      // Navigate to the project route using slug
      navigate(`/dealroom/${slug}`);
      
      // Load user permissions for the project
      if (user?.id) {
        try {
          const result = await permissionService.getUserPermissions(user.id, projectId);
          if (result && result.data) {
            setUserRole(result.data.role || 'viewer');
          } else {
            setUserRole('viewer'); // Default role if no data
          }
        } catch (error) {
          console.error('Error loading user permissions:', error);
          setUserRole('viewer'); // Default role on error
        }
      }

      // Reset breadcrumb and search
      setBreadcrumb([]);
      setSearchTerm('');

      // Load project data
      await Promise.all([
        permissionManagement.loadUsers(),
        permissionManagement.loadGroups(),
        activityFeed.refresh()
      ]);
    } catch (error) {
      console.error('Error selecting project:', error);
      addMessage('Failed to load project data', 'error');
    }
  }, [navigate, user, projectManagement, permissionManagement, activityFeed, addMessage]);

  // Handle create project
  const handleCreateProject = useCallback(async (projectData) => {
    try {
      const newProject = await projectManagement.createProject(projectData);
      
      if (newProject) {
        addMessage('Project created successfully!', 'success');
        await handleProjectSelect(newProject.id);
        closeModal('projectCreation');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      addMessage('Failed to create project', 'error');
    }
  }, [projectManagement, handleProjectSelect, addMessage, closeModal]);

  // Load question count
  const loadQuestionCount = useCallback(async () => {
    if (!selectedProjectId) return;

    try {
      const { data, error } = await supabase
        .from('questions')
        .select('id', { count: 'exact', head: true })
        .eq('module_type', 'dataroom')
        .eq('module_id', selectedProjectId);

      if (error) throw error;
      
      return data?.length || 0;
    } catch (error) {
      console.error('Error loading question count:', error);
      return 0;
    }
  }, [selectedProjectId]);

  // Handle project color change
  const handleProjectColorChange = useCallback(async (projectId, color) => {
    try {
      await projectManagement.updateProject(projectId, { color });
      addMessage('Project color updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating project color:', error);
      addMessage('Failed to update project color', 'error');
    }
  }, [projectManagement, addMessage]);

  // Computed values
  const selectedProject = projectManagement.projects.find(p => p.id === selectedProjectId);
  const canCreate = userRole === 'owner' || userRole === 'admin';
  const productRole = userRole;

  // Load user data on mount
  useEffect(() => {
    loadUserData();
  }, [loadUserData]); // Only run once on mount

  // Projects are automatically loaded by useProjectManagement when userId changes
  // No need to manually load projects here

  // Load user role when project is selected
  useEffect(() => {
    if (user && selectedProjectId) {
      const loadUserRoleData = async () => {
        try {
          // Call permission service directly to avoid circular dependencies
          const result = await permissionService.getUserPermissions(user.id, selectedProjectId);
          if (result && result.data) {
            setUserRole(result.data.role || 'viewer');
          } else {
            setUserRole('viewer'); // Default role if no data
          }
        } catch (error) {
          console.error('Error loading user permissions:', error);
          setUserRole('viewer'); // Default role on error
          addMessage('Failed to load user permissions', 'error');
        }
      };
      loadUserRoleData();
    }
  }, [user, selectedProjectId, addMessage]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        initUser(); // Use global store to handle user initialization
      } else if (event === 'SIGNED_OUT') {
        setUserRole(null);
        setSelectedProjectId(null);
        setBreadcrumb([]);
        setSearchTerm('');
        setModals({});
      }
    });

    return () => subscription.unsubscribe();
  }, [initUser]);

  // Use global store loading state
  const isLoading = userLoading;

  return {
    // User state
    user,
    userProfile,
    userRole,
    loading: isLoading,

    // Project state
    projects: projectManagement.projects,
    selectedProjectId,
    selectedProject,
    canCreate,
    productRole,

    // UI state
    messages,
    breadcrumb,
    searchTerm,
    modals,

    // Actions
    addMessage,
    openModal,
    closeModal,
    handleProjectSelect,
    handleCreateProject,
    loadQuestionCount,
    handleProjectColorChange,
    setBreadcrumb,
    setSearchTerm,
    setModals,

    // Permission management
    ...permissionManagement,

    // Activity feed
    ...activityFeed,

    // Project management
    ...projectManagement
  };
};

export default useDataroomApp;